import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CreditCard, ShieldCheck, X, Check } from "lucide-react";

/**
 * Mock Tilopay hosted-checkout page. Only used when TILOPAY_MOCK=true (preview).
 * Presents a fake "Pay $X" button that hits /api/billing/mock-complete and then
 * redirects to /payment/success (mirroring the real Tilopay hosted UX shape).
 *
 * Real Tilopay checkout is a hosted URL on their domain — we never render
 * their form ourselves. This page ONLY exists for sandbox/demo mode.
 */
export default function MockCheckout() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const session_id = params.get("session_id");
  const order_id = params.get("order_id");
  const amount = params.get("amount");

  useEffect(() => {
    if (!session_id) nav("/payment/cancel", { replace: true });
  }, [session_id, nav]);

  const complete = async (outcome) => {
    setBusy(outcome);
    try {
      await api.post("/billing/mock-complete", { session_id, outcome });
      nav(outcome === "success" ? `/payment/success?session_id=${session_id}` : "/payment/cancel", { replace: true });
    } catch (e) {
      alert("Mock complete failed");
      setBusy(null);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", display: "grid", placeItems: "center", padding: 20,
      background: "linear-gradient(180deg, #0e0e0e 0%, #0a0a0a 100%)",
    }} data-testid="mock-checkout-page">
      <div className="card p-6" style={{ width: "100%", maxWidth: 420 }}>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={16} style={{ color: "var(--green)" }} />
          <span className="label-tiny" style={{ color: "var(--green)" }}>Tilopay · Sandbox</span>
        </div>
        <div className="text-[22px] font-bold uppercase tracking-tight mt-1">Complete your payment</div>
        <div className="label-tiny mt-1">Order {order_id}</div>

        <div className="divider my-5" />
        <div className="flex items-center justify-between">
          <span className="label">Amount due</span>
          <span className="text-[24px] font-bold">${amount}</span>
        </div>
        <div className="label-tiny mt-1" style={{ color: "var(--text-dim)" }}>
          Monthly recurring · USD · No real card will be charged in sandbox.
        </div>

        <div className="card p-4 mt-5" style={{ background: "var(--bg-elev)" }}>
          <div className="flex items-center gap-3">
            <CreditCard size={18} />
            <div>
              <div className="font-semibold text-[13px]">Test card</div>
              <div className="label-tiny mono">4111 1111 1111 1111 · any expiry · any CVV</div>
            </div>
          </div>
        </div>

        <button onClick={() => complete("success")} disabled={!!busy}
                className="btn btn-primary w-full mt-5" data-testid="mock-pay-btn">
          {busy === "success" ? "Processing…" : <><Check size={14}/> Pay ${amount}</>}
        </button>
        <button onClick={() => complete("cancel")} disabled={!!busy}
                className="btn btn-outline w-full mt-2" data-testid="mock-cancel-btn">
          {busy === "cancel" ? "…" : <><X size={14}/> Cancel</>}
        </button>
      </div>
    </div>
  );
}
